import { useMemo } from 'react'

import { DateTime } from 'luxon'

import { sortArrayBy } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'

import { formatEditionDeadlines } from '@functions/publicationEdition'
import type { EntityOptions, ModuleState } from '@redux/modules/types'

import useSelector from './useSelector'

const watchEntityKeys = ['publicationEditions']

export interface PublicationEditionDeadline {
  deadlineDate: object,
  deadlineType: string,
  orderItemCount: number,
  publicationDate: object,
  publicationId: number,
  relativeDate: string | null,
  specialDeadline: boolean,
  title: string,
}

export interface FilterOptions {
  days_from_now: string,
  type?: string,
  include_unused_editions?: boolean,
}

interface UseEntitiesOptions {
  entityKey?: string,
  filters?: FilterOptions,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function usePublicationEditionsDeadlines(options: UseEntitiesOptions = {}) {
  const {
    entityKey, filters, performHttpRequests, requestOptions,
  } = options

  const { days_from_now, type: filterType, include_unused_editions: filterIncludeUnusedEditions } = filters || {}

  const filterDaysFromNow = days_from_now ? Number(days_from_now) : undefined

  const {
    updatedEntities: { publicationEditions: publicationEditionsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const publicationEditions = useSelector(reduxState => reduxState.entities.publicationEditions)

  const filteredPublicationEditionDeadlines = useMemo(() => {
    const deadlinesArray = Object.values(publicationEditions)
      .map(edition => formatEditionDeadlines(edition).filter((deadline) => {
        const { deadlineDate, deadlineType, orderItemCount } = deadline

        const matchUnusedEditions = filterIncludeUnusedEditions === false ? orderItemCount > 0 : true

        const day = DateTime.now().plus({ days: filterDaysFromNow })
        // @ts-expect-error TS2339
        const matchDay = filterDaysFromNow !== undefined ? day.toISODate() === deadlineDate?.toISODate() : true
        const matchType = filterType ? filterType === deadlineType : true

        return matchDay && matchType && matchUnusedEditions
      }))

    const filtered = deadlinesArray.flat()

    return sortArrayBy(filtered, 'asc', edition => edition.deadlineDate)
  }, [publicationEditionsUpdatedAt, JSON.stringify(filters)])

  const filteredPublicationEditionDeadlinesCount = filteredPublicationEditionDeadlines.length
  const hasFilteredPublicationEditionDeadlines = !!filteredPublicationEditionDeadlinesCount

  const specialDeadlines = filteredPublicationEditionDeadlines.filter(edition => edition.specialDeadline)
  const standardDeadlines = filteredPublicationEditionDeadlines.filter(edition => !edition.specialDeadline)

  const { loading: loadingPublicationEditions } = useReduxAction(
    'publicationEditionDeadlines',
    'loadPublicationEditionDeadlines',
    {
      entityKey,
      ...filters,
      ...requestOptions,
    },
    [performHttpRequests, filters],
    {
      // @ts-expect-error TS2322
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    filteredPublicationEditionDeadlines,
    filteredPublicationEditionDeadlinesCount,
    hasFilteredPublicationEditionDeadlines,
    loading: loadingPublicationEditions,
    specialDeadlines,
    standardDeadlines,
  }
}

export default usePublicationEditionsDeadlines
